import api from '@api/api';
import { serializeSearchParams } from '@api/searchParams';
import type { ListQuery } from '@models/listQuery';
import { Lookup } from '@models/lookup';
import { PagedResult } from '@models/pagedResult';
import { createCache, createResourceGroup } from 'cross-state';
import { ZodType } from 'zod';

export interface WithId {
  id: string;
}

export default abstract class CrudApi<
  TItem extends WithId,
  TCreate = TItem,
  TUpdate extends WithId = Partial<TCreate> & WithId,
> {
  abstract readonly path: string;
  abstract readonly model: ZodType<TItem, any, any>;
  readonly caches = createResourceGroup();

  list = createCache(
    async (query?: ListQuery) => {
      const searchParams = serializeSearchParams(query);
      const res = await api.get(this.path, { searchParams }).json();
      const items = PagedResult<TItem>(this.model).parse(res);

      for (const item of items.items) {
        const { status, isUpdating } = this.get(item.id).state.get();
        if (status === 'pending' && !isUpdating) {
          this.get(item.id).updateValue(item);
          this.get(item.id).invalidate();
        }
      }

      return items;
    },
    { resourceGroup: this.caches },
  );

  all = createCache(
    async (query?: Omit<ListQuery, 'skip' | 'limit'>) => {
      let skip = 0;
      const limit = 100;
      const items: TItem[] = [];
      let res;

      do {
        res = await this.list({ ...query, skip, limit }).get();
        items.push(...res.items);
        skip += limit;
      } while (res.hasMore);

      return items;
    },
    {
      resourceGroup: this.caches,
    },
  );

  lookup = this.all.mapCache((x) => Lookup.from(x));

  get = createCache(
    async (id: string) => {
      const res = await api.get(`${this.path}/${id}`).json();
      return this.model.parse(res);
    },
    {
      resourceGroup: this.caches,
    },
  );

  async create(item: TCreate) {
    const res = await api.post(this.path, { json: item }).json();
    const updated = this.model.parse(res);

    this.list.invalidateAll();
    this.all.invalidateAll();
    this.get(updated.id).updateValue(updated);

    return updated;
  }

  async update(item: TUpdate) {
    const res = await api.put(`${this.path}/${item.id}`, { json: item }).json();
    const updated = this.model.parse(res);

    this.list.invalidateAll();
    this.all.invalidateAll();
    this.get(updated.id).updateValue(updated);

    return updated;
  }

  /** After `orgId` was deleted: its own lists are left alone, refetching them would be refused with 403. */
  invalidateAllExcept(orgId: string) {
    const filter = (cache: { args: [{ orgId?: string }?] }) => cache.args[0]?.orgId !== orgId;
    this.list.invalidateAll({ filter });
    this.all.invalidateAll({ filter });
    this.get.invalidateAll();
  }

  async delete(id: string) {
    await api.delete(`${this.path}/${id}`);

    this.list.invalidateAll();
    this.all.invalidateAll();

    setTimeout(() => {
      this.get(id).clear();
    }, 1000);
  }
}
